/**
 * WhatsApp Business API (WABA) Error Codes
 *
 * Static map of all known WABA/Cloud API error codes with descriptions,
 * solutions, HTTP status codes, and categories.
 *
 * Source: https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/
 */

export interface WabaErrorDetail {
  code: number;
  message: string;
  description?: string;
  solutions?: string;
  httpStatus?: number;
  category?: string;
}

export const WABA_ERROR_CODES: Record<number, WabaErrorDetail> = {
  // ── Integrity errors ──────────────────────────────────────────
  368: {
    code: 368,
    message: 'Temporarily blocked for policies violations',
    description: 'The WhatsApp Business Account associated with the app has been restricted or disabled for violating a platform policy.',
    solutions: 'See the Policy Enforcement document to learn about policy violations and how to resolve them.',
    httpStatus: 403,
    category: 'Integrity',
  },
  130497: {
    code: 130497,
    message: 'Business account is restricted from messaging users in this country',
    description: 'The WhatsApp Business Account is restricted from messaging to users in certain countries.',
    solutions: 'See WhatsApp Business Messaging Policy for details on allowed countries for messaging in your business category.',
    httpStatus: 403,
    category: 'Integrity',
  },
  131031: {
    code: 131031,
    message: 'Account has been locked',
    description: 'The WhatsApp Business Account associated with the app has been restricted or disabled for violating a platform policy, or we were unable to verify data included in the request against data set on the WhatsApp Business Account (e.g., the two-step pin included in the request is incorrect).',
    solutions: 'See the Policy Enforcement document to learn about policy violations and how to resolve them. You can also use the Health Status API, which may provide additional insight into the reason or reasons for the account lock.',
    httpStatus: 403,
    category: 'Integrity',
  },

  // ── Template creation errors ──────────────────────────────────
  2388040: {
    code: 2388040,
    message: 'Character limit exceeded',
    description: 'A field in your template has exceeded the maximum character limit allowed.',
    solutions: 'Refer to the error message for specific details on the affected field and its corresponding character limits.',
    category: 'Template Creation',
  },
  2388047: {
    code: 2388047,
    message: 'Message header format is incorrect',
    description: 'Your message header contains invalid formatting.',
    solutions: 'Refer to the error message for specific details on valid formatting.',
    category: 'Template Creation',
  },
  2388072: {
    code: 2388072,
    message: 'Message body format is incorrect',
    description: 'Your message body contains invalid formatting.',
    solutions: 'Refer to the error message for specific details on valid formatting.',
    category: 'Template Creation',
  },
  2388073: {
    code: 2388073,
    message: 'Message footer format is incorrect',
    description: 'Your message footer contains invalid formatting.',
    solutions: 'Refer to the error message for specific details on valid formatting.',
    category: 'Template Creation',
  },
  2388293: {
    code: 2388293,
    message: 'Parameters words ratio exceeds limit',
    description: 'This template has too many variables for its length. Reduce the number of variables or increase the message length.',
    solutions: 'Refer to the error message for specific details on valid formatting.',
    category: 'Template Creation',
  },
  2388299: {
    code: 2388299,
    message: 'Leading or trailing parameters not allowed',
    description: 'Variables cannot be at the start or end of the template.',
    solutions: 'Refer to the error message for specific details on valid formatting.',
    category: 'Template Creation',
  },

  // ── Send template errors ──────────────────────────────────────
  2388019: {
    code: 2388019,
    message: 'Message Template Limit Exceeded',
    description: 'You have exceeded the maximum number of message templates you can have for this WhatsApp business account.',
    solutions: 'A WhatsApp Business Account can have up to 250 message templates. See Template limits.',
    category: 'Template',
  },

  // ── Phone migration errors ────────────────────────────────────
  2388012: {
    code: 2388012,
    message: 'This phone number already exists in your list of phone numbers',
    description: 'The phone number you are trying to migrate is already present in your WhatsApp account.',
    solutions: 'Try again with a phone number that is not already present in your WhatsApp account.',
    category: 'Phone Migration',
  },
  2388091: {
    code: 2388091,
    message: 'Phone number not eligible for registration code',
    description: 'Phone ownership verification APIs are not available for this use case.',
    solutions: 'Register and verify the number.',
    category: 'Phone Migration',
  },
  2388093: {
    code: 2388093,
    message: 'Phone number not eligible for verification code',
    description: 'Phone ownership verification APIs are not available for this use case.',
    solutions: 'Register and verify the number.',
    category: 'Phone Migration',
  },
  2388103: {
    code: 2388103,
    message: 'Cannot migrate phone number',
    description: 'Various issues can prevent phone number migration including missing webhooks, unapproved display names, incorrect account setup, or missing payment accounts.',
    solutions: '* Subscribe your app to webhooks on the destination WhatsApp Business account\n* Register and verify the number\n* Get your business phone number\'s display name approved\n* Set up a credit line and share it with the business customer\n* Ensure business verification is completed\n* Migrate the phone number into a WhatsApp Business Account that is messaging for the same business',
    category: 'Phone Migration',
  },
  2494100: {
    code: 2494100,
    message: 'Account is in maintenance mode',
    description: 'The business phone number is in maintenance mode.',
    solutions: 'Try again in a few minutes.',
    category: 'Phone Migration',
  },

  // ── Template insights errors ──────────────────────────────────
  200005: {
    code: 200005,
    message: 'Template insights unavailable',
    description: 'Template insights are not available yet for this WhatsApp Business account.',
    solutions: 'You are unable to enable template insights for this WhatsApp Business account at the moment.',
    category: 'Template Insights',
  },
  200006: {
    code: 200006,
    message: 'Cannot disable template insights',
    description: 'Invalid operation. Template Insights cannot be disabled once enabled.',
    solutions: 'Template insights cannot be disabled once enabled for a WhatsApp Business account.',
    category: 'Template Insights',
  },
  200007: {
    code: 200007,
    message: 'Template Insights not enabled',
    description: 'Template Insights have not been enabled for this WhatsApp Business Account.',
    solutions: 'To enable template insights, see Confirming template analytics.',
    category: 'Template Insights',
  },

  // ── WhatsApp Business Account errors ──────────────────────────
  2593079: {
    code: 2593079,
    message: 'WABA marked for migration to different solution ID',
    description: 'This WABA has already been marked for migration to a different solution ID.',
    solutions: 'The OBO account ownership model is now deprecated. Please contact support.',
    category: 'Account',
  },
  2593085: {
    code: 2593085,
    message: 'Invalid WhatsApp Business Account for OBO Mobility',
    description: 'The WABA is not eligible for OBO ownership transfer. Possible reasons: the WABA is already owned by the business customer, or the business customer has yet to accept the OBO request in the Meta Business Suite.',
    solutions: 'The OBO account ownership model is now deprecated. Please contact support.',
    category: 'Account',
  },

  // ── Synchronization errors ────────────────────────────────────
  2593107: {
    code: 2593107,
    message: 'Synchronization Request limit exceeded',
    description: 'You have exceeded the maximum number of times to call the synchronization API for this phone number.',
    solutions: 'You can only call this endpoint once to synchronize the business phone number contacts and once to synchronize its messaging history. Offboard the business customer and re-onboard them.',
    httpStatus: 400,
    category: 'Synchronization',
  },
  2593108: {
    code: 2593108,
    message: 'Synchronization Request made outside of allowed time window',
    description: 'Synchronization request can only be made within 24 hours of onboarding.',
    solutions: 'You can only initiate contacts and messaging history synchronization of an onboarded WhatsApp Business app user within 24 hours of onboarding the user. Offboard the user and re-onboard them.',
    httpStatus: 400,
    category: 'Synchronization',
  },

  // ── Throttling errors ─────────────────────────────────────────
  4: {
    code: 4,
    message: 'API Too Many Calls',
    description: 'The app has reached its API call rate limit.',
    solutions: 'Load the app in the App Dashboard and view the Application Rate Limit section to verify that the app has reached its rate limit. If it has, try again later or reduce the frequency or amount of API queries the app is making.',
    httpStatus: 400,
    category: 'Throttling',
  },
  80007: {
    code: 80007,
    message: 'Rate limit issues',
    description: 'The WhatsApp Business Account has reached its rate limit.',
    solutions: 'See WhatsApp Business Account Rate Limits. Try again later or reduce the frequency or amount of API queries the app is making.',
    httpStatus: 400,
    category: 'Throttling',
  },
  130429: {
    code: 130429,
    message: 'Rate limit hit',
    description: 'Cloud API message throughput has been reached.',
    solutions: 'The app has reached the API\'s throughput limit. See Throughput. Try again later or reduce the frequency with which the app sends messages.',
    httpStatus: 400,
    category: 'Throttling',
  },
  131048: {
    code: 131048,
    message: 'Spam rate limit hit',
    description: 'Message failed to send because there are restrictions on how many messages can be sent from this phone number. This may be because too many previous messages were blocked or flagged as spam.',
    solutions: 'Check your quality status in the WhatsApp Manager. See Template limits and Template quality.',
    httpStatus: 400,
    category: 'Throttling',
  },
  131056: {
    code: 131056,
    message: '(Business Account, Consumer Account) pair rate limit hit',
    description: 'Too many messages sent from the sender phone number to the same recipient phone number in a short period of time.',
    solutions: 'Wait and retry the operation, if you intend to send messages to the same phone number. You can still send messages to a different phone number without waiting.',
    httpStatus: 400,
    category: 'Throttling',
  },
  133016: {
    code: 133016,
    message: 'Account register deregister rate limit exceeded',
    description: 'Registration or Deregistration failed because there were too many attempts for this phone number in a short period of time.',
    solutions: 'The business phone number is being blocked because it has reached its registration/deregistration attempt limit. Try again once the number is unblocked.',
    httpStatus: 400,
    category: 'Throttling',
  },

  // ── Other / General errors ────────────────────────────────────
  1: {
    code: 1,
    message: 'API Unknown',
    description: 'Invalid request or possible server error.',
    solutions: 'Check the WhatsApp Business Platform Status page to see API status information. If there are no server outages, check the endpoint reference and verify that your request is formatted correctly and meets all endpoint requirements.',
    httpStatus: 400,
    category: 'General',
  },
  2: {
    code: 2,
    message: 'API Service',
    description: 'Temporary due to downtime or due to being overloaded.',
    solutions: 'Check the WhatsApp Business Platform Status page to see API status information before trying again.',
    httpStatus: 503,
    category: 'General',
  },
  33: {
    code: 33,
    message: 'Parameter value is not valid',
    description: 'The business phone number has been deleted.',
    solutions: 'Verify that the business phone number is correct.',
    httpStatus: 400,
    category: 'General',
  },
  100: {
    code: 100,
    message: 'Invalid parameter',
    description: 'The request included one or more unsupported or misspelled parameters.',
    solutions: '* See the endpoint\'s reference to determine which parameters are supported and how they are spelled\n* For WhatsApp Flows with Endpoint — ensure when setting the business public key, it is a valid 2048-bit RSA public key in PEM format\n* Ensure there is no mismatch between the phone number ID you are registering and a previously stored phone number ID\n* Ensure your parameter is under any length restriction for the type',
    httpStatus: 400,
    category: 'General',
  },
  130472: {
    code: 130472,
    message: 'User\'s number is part of an experiment',
    description: 'Message was not sent as part of an experiment.',
    solutions: 'See Marketing Message Experiment.',
    httpStatus: 400,
    category: 'General',
  },
  131000: {
    code: 131000,
    message: 'Something went wrong',
    description: 'Message failed to send due to an unknown error.',
    solutions: 'Try again. If the error persists, open a Direct Support ticket.',
    httpStatus: 500,
    category: 'General',
  },
  131005: {
    code: 131005,
    message: 'Access denied',
    description: 'Permission is either not granted or has been removed.',
    solutions: 'Use the access token debugger to verify that your app has been granted the permissions required by the endpoint.',
    httpStatus: 403,
    category: 'General',
  },
  131008: {
    code: 131008,
    message: 'Required parameter is missing',
    description: 'The request is missing a required parameter.',
    solutions: 'See the endpoint\'s reference to determine which parameters are required.',
    httpStatus: 400,
    category: 'General',
  },
  131009: {
    code: 131009,
    message: 'Parameter value is not valid',
    description: 'One or more parameter values are invalid.',
    solutions: 'See the endpoint\'s reference to determine which values are supported for each parameter, and see Phone Numbers to learn how to add a phone number to a WhatsApp Business Account.',
    httpStatus: 400,
    category: 'General',
  },
  131016: {
    code: 131016,
    message: 'Service unavailable',
    description: 'A service is temporarily unavailable.',
    solutions: 'Check the WhatsApp Business Platform Status page to see API status information before trying again.',
    httpStatus: 500,
    category: 'General',
  },
  131021: {
    code: 131021,
    message: 'Recipient cannot be sender',
    description: 'Sender and recipient phone number is the same.',
    solutions: 'Send a message to a phone number different from the sender.',
    httpStatus: 400,
    category: 'General',
  },
  131026: {
    code: 131026,
    message: 'Message Undeliverable',
    description: 'Unable to deliver message. Reasons can include: the recipient phone number is not a WhatsApp phone number, recipient has not accepted the new Terms of Service and Privacy Policy, or recipient is using an old WhatsApp version.',
    solutions: '* Confirm that the recipient can actually send a message to your WhatsApp business phone number\n* Ask the recipient to accept the latest Terms of Service (Settings > Help will prompt them)\n* Ask the recipient to update to the latest version of the WhatsApp client',
    httpStatus: 400,
    category: 'Delivery',
  },
  131037: {
    code: 131037,
    message: 'Display name approval needed',
    description: 'The 555 business phone number used to send the request does not have an approved display name.',
    solutions: 'Change the 555 business phone number\'s display name.',
    httpStatus: 400,
    category: 'General',
  },
  131042: {
    code: 131042,
    message: 'Business eligibility payment issue',
    description: 'There was an error related to your payment method.',
    solutions: '* Verify that you have set up billing correctly\n* Payment account is attached to a WhatsApp Business Account\n* Credit line is not over the limit\n* Credit line (Payment Account) is set and active\n* WhatsApp Business Account is not deleted or suspended\n* Timezone and currency are set\n* MessagingFor request (On Behalf Of) is not pending or declined',
    httpStatus: 400,
    category: 'Billing',
  },
  131045: {
    code: 131045,
    message: 'Incorrect certificate',
    description: 'Message failed to send due to a phone number registration error.',
    solutions: 'Register the phone number before trying again.',
    httpStatus: 500,
    category: 'General',
  },
  131047: {
    code: 131047,
    message: 'Re-engagement message',
    description: 'More than 24 hours have passed since the recipient last replied to the sender number.',
    solutions: 'Send the recipient a template message instead.',
    httpStatus: 400,
    category: 'Delivery',
  },
  131049: {
    code: 131049,
    message: 'Meta chose not to deliver',
    description: 'This message was not delivered to maintain healthy ecosystem engagement.',
    solutions: 'If you receive this error code, wait at least 24 hours before resending the template message. Doing so will only result in another error response since the limit may be in effect for differing periods of time. See Per-User Marketing Template Message Limits for additional information.',
    httpStatus: 400,
    category: 'Delivery',
  },
  131050: {
    code: 131050,
    message: 'User has stopped receipt of marketing messages',
    description: 'Unable to deliver the message. This recipient has chosen to stop receiving marketing messages on WhatsApp from your business.',
    solutions: 'Do not retry sending messages to this user as they will not be received. To be notified whenever a WhatsApp user stops or resumes delivery of marketing template messages from your business, subscribe to the user_preferences webhook.',
    httpStatus: 400,
    category: 'Delivery',
  },
  131051: {
    code: 131051,
    message: 'Unsupported message type',
    description: 'Unsupported message type.',
    solutions: 'See Messages for supported message types before trying again with a supported message type.',
    httpStatus: 400,
    category: 'General',
  },
  131052: {
    code: 131052,
    message: 'Media download error',
    description: 'Unable to download the media sent by the user.',
    solutions: 'Ask the WhatsApp user to send you the media file using a non-WhatsApp method.',
    httpStatus: 400,
    category: 'Media',
  },
  131053: {
    code: 131053,
    message: 'Media upload error',
    description: 'Unable to upload the media used in the message. This may be due to an unsupported media type.',
    solutions: 'Inspect any media files that are causing errors and confirm that they are in fact supported. See Supported Media Types.',
    httpStatus: 400,
    category: 'Media',
  },
  131055: {
    code: 131055,
    message: 'Method not allowed',
    description: 'Only marketing template messages are supported.',
    solutions: 'Try sending again using a marketing template message.',
    httpStatus: 400,
    category: 'General',
  },
  131057: {
    code: 131057,
    message: 'Account in maintenance mode',
    description: 'Business Account is in maintenance mode. One reason for this could be that the account is undergoing a throughput upgrade.',
    solutions: 'Wait for maintenance to complete and try again.',
    httpStatus: 500,
    category: 'General',
  },
  132000: {
    code: 132000,
    message: 'Template Param Count Mismatch',
    description: 'The number of variable parameter values included in the request did not match the number of variable parameters defined in the template.',
    solutions: 'Make sure the request includes values for all of the parameters required by the template.',
    httpStatus: 400,
    category: 'Template',
  },
  132001: {
    code: 132001,
    message: 'Template does not exist',
    description: 'The template does not exist in the specified language or the template has not been approved.',
    solutions: 'Make sure your template has been approved and the template name and language locale are correct.',
    httpStatus: 404,
    category: 'Template',
  },
  132005: {
    code: 132005,
    message: 'Template Hydrated Text Too Long',
    description: 'Translated text is too long.',
    solutions: 'Check the WhatsApp Manager to verify that your template has been translated. See the Template quality document to learn how to check the status of your template.',
    httpStatus: 400,
    category: 'Template',
  },
  132007: {
    code: 132007,
    message: 'Template Format Character Policy Violated',
    description: 'Template content violates a WhatsApp policy.',
    solutions: 'See the Template review document to learn about possible reasons for the violation.',
    httpStatus: 400,
    category: 'Template',
  },
  132012: {
    code: 132012,
    message: 'Template Parameter Format Mismatch',
    description: 'Variable parameter values formatted incorrectly.',
    solutions: 'The variable parameter values included in the request are not using the format specified in the template. See the Templates document to learn more about template parameters and formats.',
    httpStatus: 400,
    category: 'Template',
  },
  132015: {
    code: 132015,
    message: 'Template is Paused',
    description: 'Template is paused due to low quality so it cannot be sent in a template message.',
    solutions: 'Edit the template to improve its quality and try again once it is approved.',
    httpStatus: 400,
    category: 'Template',
  },
  132016: {
    code: 132016,
    message: 'Template is Disabled',
    description: 'Template has been paused too many times due to low quality and is now permanently disabled.',
    solutions: 'Create a new template with different content.',
    httpStatus: 400,
    category: 'Template',
  },
  132018: {
    code: 132018,
    message: 'Template validation error',
    description: 'There\'s an issue with the parameters in your template.',
    solutions: 'Review the errors, update the parameters as needed, and resend your message using a correctly configured template.',
    httpStatus: 400,
    category: 'Template',
  },
  132068: {
    code: 132068,
    message: 'Flow is blocked',
    description: 'Flow is in blocked state.',
    solutions: 'Correct the Flow.',
    httpStatus: 400,
    category: 'Flows',
  },
  132069: {
    code: 132069,
    message: 'Flow is throttled',
    description: 'Flow is in throttled state and 10 messages using this flow were already sent in the last hour.',
    solutions: 'Correct the Flow.',
    httpStatus: 400,
    category: 'Flows',
  },
  133000: {
    code: 133000,
    message: 'Incomplete Deregistration',
    description: 'A previous deregistration attempt failed.',
    solutions: 'Deregister the number again before registering.',
    httpStatus: 500,
    category: 'Registration',
  },
  133004: {
    code: 133004,
    message: 'Server Temporarily Unavailable',
    description: 'Server is temporarily unavailable.',
    solutions: 'Check the WhatsApp Business Platform Status page to see API status information and check the response details value before trying again.',
    httpStatus: 503,
    category: 'General',
  },
  133005: {
    code: 133005,
    message: 'Two step verification PIN Mismatch',
    description: 'Two-step verification PIN incorrect.',
    solutions: 'Verify that the two-step verification PIN included in the request is correct. To reset the PIN, disable two-step verification, then set a new PIN.',
    httpStatus: 400,
    category: 'Registration',
  },
  133006: {
    code: 133006,
    message: 'Phone number re-verification needed',
    description: 'Phone number needs to be verified before registering.',
    solutions: 'Verify and register the phone number.',
    httpStatus: 400,
    category: 'Registration',
  },
  133008: {
    code: 133008,
    message: 'Too many two step verification PIN guesses',
    description: 'Too many two-step verification PIN guesses for this phone number.',
    solutions: 'Try again after the amount of time specified in the details response value.',
    httpStatus: 400,
    category: 'Registration',
  },
  133009: {
    code: 133009,
    message: 'Two step verification PIN guessed too fast',
    description: 'Two-step verification PIN was entered too quickly.',
    solutions: 'Check the details response value before trying again.',
    httpStatus: 400,
    category: 'Registration',
  },
  133010: {
    code: 133010,
    message: 'Phone number not registered',
    description: 'Phone number not registered on the WhatsApp Business Platform.',
    solutions: 'Register the phone number before trying again.',
    httpStatus: 400,
    category: 'Registration',
  },
  133015: {
    code: 133015,
    message: 'Please wait before re-registering',
    description: 'The phone number you are attempting to register was recently deleted, and deletion has not yet completed.',
    solutions: 'Wait 5 minutes before re-trying the request.',
    httpStatus: 400,
    category: 'Registration',
  },
  134011: {
    code: 134011,
    message: 'WhatsApp Payments terms of service not accepted',
    description: 'Message failed to send because WhatsApp Payments terms of service acceptance is pending for this WhatsApp Business Account.',
    solutions: 'Accept the WhatsApp Payments terms of service using the link provided in the error message before trying again.',
    httpStatus: 400,
    category: 'Billing',
  },
  134100: {
    code: 134100,
    message: 'Only marketing messages supported',
    description: 'You are attempting to send a utility or authentication template. Only templates categorized as MARKETING are supported.',
    solutions: 'Only send templates categorized as MARKETING.',
    httpStatus: 400,
    category: 'Marketing Messages API',
  },
  134101: {
    code: 134101,
    message: 'Your template is still syncing',
    description: 'When you send a message from a template, the template syncing process can take up to 10 minutes to complete.',
    solutions: 'Wait 10 minutes and try sending your message again.',
    httpStatus: 400,
    category: 'Marketing Messages API',
  },
  134102: {
    code: 134102,
    message: 'Template unavailable for use',
    description: 'We were unable to complete Ad synchronization for the template you are attempting to send, or you may not be eligible for MM API for WhatsApp.',
    solutions: 'Check your eligibility status. If the WhatsApp Business Account\'s marketing_messages_lite_api_status value is ONBOARDED and the problem persists, please contact support.',
    httpStatus: 500,
    category: 'Marketing Messages API',
  },
  135000: {
    code: 135000,
    message: 'Generic user error',
    description: 'Message failed to send because of an unknown error with your request parameters.',
    solutions: 'See the endpoint\'s reference to determine if you are querying the endpoint using the correct syntax. Contact customer support if you continue receiving this error code.',
    httpStatus: 400,
    category: 'General',
  },
  1752041: {
    code: 1752041,
    message: 'Duplicate Request',
    description: 'Duplicate Request is thrown when a client has already been invited to onboard by any partner.',
    solutions: 'Onboarding requests are limited to one per business customer. If you receive an error indicating that the onboarding request has already been made, no further action is required, as all eligible WABAs for that client will be onboarded without additional steps.',
    httpStatus: 400,
    category: 'Marketing Messages API',
  },
};

/** Look up a WABA error detail by code (synchronous, static map). */
export function getWabaErrorDetail(code: number): WabaErrorDetail | null {
  return WABA_ERROR_CODES[code] || null;
}
